/**
 * UranIA PA — Login Pubblica Amministrazione.
 *
 * 2026-09-06: sostituita con l'implementazione del documento di consegna
 * "UranIA PA — implementazione delle quattro schermate" (handoff esterno),
 * su richiesta esplicita dell'utente di applicarlo letteralmente. Rende
 * `features/auth/LoginPa.tsx` invece della versione precedente (colonna
 * hero editoriale + `SpidButton`/`AltIdentityButton` reali).
 *
 * 2026-09-23: la selezione IdP non passa più `kc_idp_hint` letterale
 * (`'spid'|'cie'|'cns'`, mai risolto contro il realm reale) né un
 * `acr_values` inventato — ora riusa `IDP_HINT_OPTIONS` (derivato da
 * `lib/auth/idp-options.ts`, la stessa fonte usata da `/login/beneficiari`
 * via `SpidButton`/`AltIdentityButton`) e `startFederatedLogin` (stesso
 * modulo di `login-actions.ts`). Un solo posto da correggere se gli alias
 * Keycloak cambiano. Il bottone SPID resta unico (nessun dropdown a 9 IdP,
 * scelta invariata dell'handoff): usa il primo di `SPID_IDPS`, che in
 * staging puntano tutti allo stesso alias (vedi commento in `idp-options.ts`).
 *
 * getParentRoute/path restano quelli esistenti: solo il componente reso
 * cambia.
 */

import { useCallback, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import type { JSX } from 'react';

import { LoginPa, type IdpHint } from '../../features/auth/LoginPa.tsx';
import { ALT_IDENTITY_OPTIONS, SPID_IDPS } from '../../lib/auth/idp-options.ts';
import { startFederatedLogin } from '../../lib/auth/login-actions.ts';
import { Route as PubblicoRoute } from '../_pubblico.tsx';

// SPID_IDPS ha sempre 9 elementi (garanzia a compile-time in idp-options.ts).
const [defaultSpidIdp] = SPID_IDPS;
if (defaultSpidIdp === undefined) {
  throw new Error('SPID_IDPS è vuoto: configurazione idp-options.ts non valida.');
}

const IDP_HINT_OPTIONS: Record<IdpHint, { keycloakAlias: string; acrValues: string }> = {
  spid: { keycloakAlias: defaultSpidIdp.keycloakAlias, acrValues: 'L2' },
  cie: ALT_IDENTITY_OPTIONS.cie,
  cns: ALT_IDENTITY_OPTIONS.cns,
};

function LoginPaRoute(): JSX.Element {
  const [pending, setPending] = useState<IdpHint | null>(null);

  const handleLogin = useCallback((idp: IdpHint): void => {
    setPending(idp);
    void startFederatedLogin(IDP_HINT_OPTIONS[idp]).catch(() => {
      setPending(null);
    });
  }, []);

  return <LoginPa onLogin={handleLogin} pending={pending} />;
}

export const Route = createRoute({
  getParentRoute: () => PubblicoRoute,
  path: '/login/pa',
  component: LoginPaRoute,
});
